import React, { useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import styles from '@components/organisms/campaign/campaign.module.scss';
import { Autoplay, EffectCreative, EffectFade, Lazy, Pagination } from 'swiper';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import useWindowSize from '@hooks/use-window-size';
import { CampaignBlock as ICampaignBlock, CategoryContainer } from '@interfaces/models/campaign';
import CampaignBlock from '@components/molecules/campaign-block/campaign-block';
import { useTranslation } from 'next-i18next';
import { useSwiperAccessibility } from '@hooks/swiper/use-swiper-accessibility';
import Button from '@components/atoms/button/button';

export const CAMPAIGN_SWIPER_ID = 'HP_campaignSwiper';
const SLIDER_ANIMATION_DURATION = 4200;
const slidesCount = {
  single: 1,
  double: 2,
  multiple: 4,
};

// Pass animation duration to inner bullet animation
const sliderStyles = {
  '--vs-slider-animation-duration': `${SLIDER_ANIMATION_DURATION}ms`,
  '--swiper-navigation-size': '25px',
} as React.CSSProperties;

interface CampaignSwiperProps {
  componentID: string;
  categoryContainer: CategoryContainer;
  campaignBlocks: ICampaignBlock[];
  sectionIdx: string;
}

const CampaignSwiper = ({ campaignBlocks, componentID, categoryContainer, sectionIdx }: CampaignSwiperProps) => {
  const { t: tA11y } = useTranslation('a11y');
  const { isWiderThanMd } = useWindowSize();

  // NAVIGATION
  const navigationPrev = useRef<HTMLButtonElement>(null);
  const navigationNext = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const { ariaLiveRef, isInViewportRef } = useSwiperAccessibility({
    getSlideLabel: (current, total) =>
      tA11y('A11Y.CMS.CAMPAIGN_CAROUSEL.CURRENT_CAMPAIGN', { currentCampaign: current, totalCampaigns: total }),
    getAriaLiveLabel: (start, end, total) =>
      tA11y('A11Y.CMS.CAMPAIGN_CAROUSEL.CURRENT_CAMPAIGN', {
        currentCampaign: start === end ? start : `${start}-${end}`,
        totalCampaigns: total,
      }),
    swiper: swiperRef.current,
    slides: campaignBlocks,
    useInert: true, // Prevent focus on hidden slides' content
    onViewportChange: (isVisible) => {
      // Control autoplay based on viewport visibility
      const swiper = swiperRef.current;
      if (!swiper?.autoplay) {
        return;
      }

      if (isVisible) {
        swiper.autoplay.start();
      } else {
        swiper.autoplay.stop();
      }
    },
  });

  const addBulletListRoles = () => {
    // Skip if not in viewport (reuse viewport tracking from hook)
    if (!isInViewportRef.current || !paginationRef.current) {
      return;
    }

    // Add list item semantics to pagination bullets
    const bullets = paginationRef.current.querySelectorAll('.swiper-pagination-bullet');

    bullets.forEach((bullet) => {
      if (!bullet.hasAttribute('role')) {
        bullet.setAttribute('role', 'listitem');
      }
    });
  };

  const handleAfterInit = (swiper: SwiperInstance): void => {
    swiperRef.current = swiper;

    // Add list semantics on init (always)
    addBulletListRoles();

    // Re-add if bullets are re-rendered during slide changes
    // Event still fires off-screen, but function checks viewport
    swiper.on('slideChange', addBulletListRoles);
  };

  const handlePrevClick = () => {
    const swiper = swiperRef?.current;
    if (!swiper) {
      return;
    }

    if (swiper.isBeginning) {
      // Quick fade when wrapping (10ms)
      swiper.slideTo(swiper.slides.length - 1, 0);
    } else {
      swiper.slidePrev();
    }
  };

  const handleNextClick = () => {
    const swiper = swiperRef?.current;
    if (!swiper) {
      return;
    }

    if (swiper.isEnd) {
      // Quick fade when wrapping (10ms)
      swiper.slideTo(0, 0);
    } else {
      swiper.slideNext();
    }
  };

  // Pause autoplay when user focuses on carousel content (WCAG 2.2.2)
  useEffect(() => {
    const swiper = swiperRef?.current;
    if (!swiper || !swiper?.el) {
      return;
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Only pause if focus is on interactive content within slides, not navigation buttons
      if (target && swiper.el.contains(target) && !target.closest('.slider_button')) {
        swiper.autoplay?.pause();
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      // Resume autoplay only if focus left the entire swiper
      if (!relatedTarget || !swiper.el.contains(relatedTarget)) {
        // Need to stop first, then start to properly resume from paused state
        swiper.autoplay?.stop();
        swiper.autoplay?.start();
      }
    };

    swiper.el.addEventListener('focusin', handleFocusIn);
    swiper.el.addEventListener('focusout', handleFocusOut);

    return () => {
      if (swiper?.el) {
        swiper.el.removeEventListener('focusin', handleFocusIn);
        swiper.el.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, [swiperRef.current]);

  return (
    <>
      <div
        className="visuallyhidden"
        aria-live="polite"
        aria-atomic="true"
        ref={ariaLiveRef}
      />
      {campaignBlocks.length >= 2 && (
        <div className={clsx(styles.slider_button)}>
          <Button
            ref={navigationPrev}
            className={clsx(styles.slider_button_prev)}
            disableDefaultStyling
            onClick={handlePrevClick}
          >
            <Icon
              name="ios-arrow-left-16"
              ariaLabel={tA11y('A11Y.ICON.ARROW_PREVIOUS')}
            />
          </Button>
          <Button
            ref={navigationNext}
            className={clsx(styles.slider_button_next)}
            disableDefaultStyling
            onClick={handleNextClick}
          >
            <Icon
              name="ios-arrow-right-16"
              ariaLabel={tA11y('A11Y.ICON.ARROW_NEXT')}
            />
          </Button>
        </div>
      )}
      <Swiper
        id={CAMPAIGN_SWIPER_ID}
        draggable
        loop={false}
        rewind={true}
        watchOverflow={false}
        className={styles.slider}
        effect={isWiderThanMd ? 'fade' : null}
        modules={[Autoplay, EffectCreative, Pagination, Lazy, EffectFade]}
        slidesPerView={1}
        style={sliderStyles}
        autoplay={{
          delay: SLIDER_ANIMATION_DURATION,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        onBeforeInit={(swiper) => {
          swiper.allowTouchMove = campaignBlocks.length > 1;
        }}
        onAfterInit={handleAfterInit}
        pagination={{
          el: `#swiper_pagination-${componentID}`,
          clickable: true,
          bulletClass: clsx('swiper-pagination-bullet', styles.slider_pagination__bullet),
          bulletActiveClass: clsx('swiper-pagination-bullet-active', styles['slider_pagination__bullet--active']),
        }}
        aria-label={tA11y('A11Y.CMS.CAMPAIGN_CAROUSEL.ALL_CAMPAIGNS', { totalCampaigns: campaignBlocks.length })}
      >
        {campaignBlocks
          ?.slice(
            0,
            isWiderThanMd
              ? campaignBlocks.length === 1
                ? slidesCount.single
                : slidesCount.multiple
              : slidesCount.multiple,
          )
          ?.map((campaignBlock, idx) => {
            const { uid: UID, displayTitle: title } = campaignBlock || {};
            const key = `${title || ''}-${UID}`;
            return (
              <SwiperSlide key={key}>
                <CampaignBlock
                  isHeroVersion={true}
                  isSingle={campaignBlocks.length === 1}
                  categoryContainer={categoryContainer}
                  {...campaignBlock}
                  sectionIdx={sectionIdx}
                  posX={String(idx + 1)}
                />
              </SwiperSlide>
            );
          })}
      </Swiper>

      {campaignBlocks.length > 1 ? (
        <div
          ref={paginationRef}
          className={styles.slider_pagination}
          id={`swiper_pagination-${componentID}`}
          style={sliderStyles}
          role="list"
          aria-label={tA11y('A11Y.CMS.CAMPAIGN_CAROUSEL.ALL_CAMPAIGNS', { totalCampaigns: campaignBlocks.length })}
        />
      ) : null}
    </>
  );
};

export default CampaignSwiper;
